import classNames from 'classnames';
import { observer } from 'mobx-react';
import React, { useContext, useEffect, useState } from 'react';

export type FieldSetProps = React.HTMLAttributes<HTMLFieldSetElement> & {
  disabled?: boolean;
  supportDisabledBackdrop?: boolean;
};

type FieldSetActions = {
  disabled: boolean;
  setIsDisabled: (disabled: boolean) => void;
};

//todo you can nest field sets, so using these contexts
//we could enable them to all share the same disabled state
//or to let a field set be configurable to do either
//but for now each field set will have it's own disable/enabled state.
export type useFieldSetContextReturn = {
  isInFieldSet: boolean;
  /** If the component is in a field set context this will contain the disabled/setDisabled fields so a child can see if it's parent field set is disabled or optionally disable it. */
  fieldSetActions?: FieldSetActions;
};

const FieldSetContext = React.createContext<useFieldSetContextReturn>({
  isInFieldSet: false,
  fieldSetActions: undefined,
});

export const useFieldSetContext = () => {
  const context = useContext(FieldSetContext);
  return context;
};

/**
 * This is a Managed FieldSet that will updated a context with it's disabled value
 * This allows child components to use this useFieldSetContext hook to determine if they are in a disabled
 * field set.
 */
export const FieldSet = observer(
  ({
    disabled: fieldSetDisabled,
    children,
    supportDisabledBackdrop,
    className,
    ...attrs
  }: FieldSetProps) => {
    const [disabled, setIsDisabled] = useState(fieldSetDisabled ?? false);

    useEffect(() => {
      setIsDisabled(fieldSetDisabled ?? false);
    }, [fieldSetDisabled]);

    return (
      <FieldSetContext.Provider
        value={{
          isInFieldSet: true,
          fieldSetActions: { disabled, setIsDisabled },
        }}
      >
        <fieldset
          disabled={disabled ?? undefined}
          className={classNames('customFieldSet', className, {
            'disabled-child-inputs': disabled,
          })}
          {...attrs}
        >
          {supportDisabledBackdrop && <div className="fieldset-backdrop"></div>}
          {children}
        </fieldset>
      </FieldSetContext.Provider>
    );
  },
);
